import type { SkillNeedTemplate } from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

const SKILL_TEMPLATES_URL = 'list/skill-need-templates'

export const useSkillTemplateCreate = (onSuccess?: () => void) => {
  const { t } = useTranslation()

  const mutation = useMutation(
    async (payload: SkillNeedTemplate) => {
      const result = await postJSON({
        url: `${SKILL_TEMPLATES_URL}/`,
        method: 'POST',
        json: payload
      })
      return result
    },
    {
      onSuccess: () => {
        showToast({ title: t('skillsAndNeeds.skillAdded') })
        onSuccess?.()
      },
      onError: (error: any) => {
        // Don't show generic toast for code 1001 — it's handled in the modal
        if (error?.code === 1001) return
        showToast({
          type: 'error',
          title: error?.message || t('skillsAndNeeds.skillAddFailed')
        })
      }
    }
  )

  return {
    createSkillTemplate: mutation.mutateAsync,
    isCreating: mutation.isLoading
  }
}

export const useSkillTemplateUpdate = (onSuccess?: () => void) => {
  const { t } = useTranslation()

  const mutation = useMutation(
    async ({ id, ...payload }: SkillNeedTemplate & { id: string }) => {
      const result = await postJSON({
        url: `${SKILL_TEMPLATES_URL}/${id}`,
        method: 'PUT',
        json: payload
      })
      return result
    },
    {
      onSuccess: () => {
        showToast({ title: t('skillsAndNeeds.skillUpdated') })
        onSuccess?.()
      },
      onError: (error: any) => {
        if (error?.code === 1001) return
        showToast({
          type: 'error',
          title: error?.message || t('skillsAndNeeds.skillUpdateFailed')
        })
      }
    }
  )

  return {
    updateSkillTemplate: mutation.mutateAsync,
    isUpdating: mutation.isLoading
  }
}

export const useSkillTemplateDelete = (onSuccess?: () => void) => {
  const { t } = useTranslation()

  const mutation = useMutation(
    async (id: string) => {
      await postJSON({
        url: `${SKILL_TEMPLATES_URL}/${id}`,
        method: 'DELETE',
        responseType: 'text'
      })
    },
    {
      onSuccess: () => {
        showToast({ title: t('skillsAndNeeds.skillDeleted') })
        onSuccess?.()
      },
      onError: (error: any) => {
        showToast({
          type: 'error',
          title: error?.message || t('skillsAndNeeds.skillDeleteFailed')
        })
      }
    }
  )

  return {
    deleteSkillTemplate: mutation.mutateAsync,
    isDeleting: mutation.isLoading
  }
}
